/**
 * measureV4 — 실치수 기반 문제/풀이 높이 측정 + 페이지 분배 (v4).
 *
 * 알고리즘은 v2(printPreview.distributeProblems)와 동일한 규칙을 유지한다:
 *  - 페이지당 좌/우 2단 × 단당 2슬롯(상단 0 / 중단 slotH), 최대 4문제
 *  - 문제 높이 > 슬롯 한계면 단 전체 점유(useFullColumn)
 *  - 첫 페이지와 이후 페이지의 슬롯 한계가 다름(헤더 차이)
 * 다른 점: 좌표계가 328mm 캔버스가 아니라 실제 용지 mm(geo)이고, 측정 폭·폰트도
 * 실인쇄 값(geo.colWPx, geo.contentFontPt)으로 측정한다.
 */
import { MM_PER_PX } from './printSpec';
import { sheetChromeCss, numberSpecV4, solutionPlaceholder, solutionItemGapMm } from './PrintSheetV4';
import { distributeByPatterns } from './pagePatterns';

// 측정용 오프스크린 렌더 — 시트 공통 CSS(sheetChromeCss)를 함께 주입해 실제 렌더와
// 동일한 규칙(이미지/박스 마진, 폰트 강제, .katex 1em)으로 높이를 잰다.
// 오버레이가 로딩 상태로 SheetChrome을 언마운트한 시점에도 측정이 어긋나지 않는다(단일 소스).
function makeMeasureHolder(innerHtml, widthPx, css) {
  const tempDiv = document.createElement('div');
  tempDiv.style.cssText = `position:absolute;visibility:hidden;left:-9999px;top:0;width:${widthPx}px;box-sizing:border-box;font-family:'Pretendard',sans-serif;line-height:1.5;`;
  tempDiv.innerHTML = `<style>${css}</style><div class="pv4-sheet">${innerHtml}</div>`;
  return tempDiv;
}

function measureHeightMm(innerHtml, widthPx, geo) {
  const tempDiv = makeMeasureHolder(innerHtml, widthPx, sheetChromeCss(geo));
  document.body.appendChild(tempDiv);
  const h = tempDiv.querySelector('.pv4-sheet').offsetHeight;
  document.body.removeChild(tempDiv);
  return h * MM_PER_PX;
}

// 여러 개를 한 번에 잰다 — 래퍼는 위와 같은 모양(<style> + .pv4-sheet)이고, CSS 본문은 첫 래퍼에만 싣는다.
// 문항마다 넣고 빼면 그때마다 문서 전체 배치를 다시 계산한다(N번). 한 번에 붙이면 첫 읽기 한 번만 계산한다.
function measureHeightsMm(entries, geo, readHeightPx = (sheet) => sheet.offsetHeight) {
  if (entries.length === 0) return [];
  const css = sheetChromeCss(geo);
  const holders = entries.map(([innerHtml, widthPx], i) => makeMeasureHolder(innerHtml, widthPx, i === 0 ? css : ''));
  const frag = document.createDocumentFragment();
  holders.forEach((el) => frag.appendChild(el));
  document.body.appendChild(frag);
  try {
    // 각 래퍼의 **자기** .pv4-sheet 만 읽는다(문서에는 화면 지면의 .pv4-sheet 도 있다). 첫 읽기 한 번만 배치를 계산한다
    return holders.map((el) => readHeightPx(el.querySelector('.pv4-sheet')) * MM_PER_PX);
  } finally {
    // 실패해도 숨은 래퍼 N개가 남아 다음 측정에 섞이지 않게 한다
    holders.forEach((el) => document.body.removeChild(el));
  }
}

function problemMeasureHtml(problem, number, geo) {
  // 번호 폰트·크기·표기·간격은 렌더(ProblemItemV4)와 동일해야 측정 정합 — B4는 "1." KoPub 700 17pt·간격 절반
  const isB4 = geo.design === 'renewalB4';
  const gapMm = (isB4 ? 3.7 : 7.4) * geo.k; // 번호-본문 간격(v2 28px, B4 절반)
  const numberFontPt = isB4 ? 17 : geo.pt(27);
  const numFont = isB4 ? "'KoPubWorldBatang','Pretendard',serif" : "'Pretendard',sans-serif";
  const numWeight = isB4 ? 700 : 900;
  const numText = isB4 ? `${number}.` : String(number).padStart(2, '0');
  const html = `
    <div class="pv4-problem-item" style="display:flex;gap:${gapMm}mm;align-items:flex-start;font-size:${geo.contentFontPt}pt;">
      <div style="font-size:${numberFontPt}pt;font-weight:${numWeight};font-family:${numFont};line-height:1;min-width:${numberFontPt}pt;white-space:nowrap;">${numText}</div>
      <div class="pv4-problem-content" style="flex:1;min-width:0;overflow-wrap:break-word;word-break:break-word;">${problem.content}</div>
    </div>`;
  return html;
}

export function measureProblemHeightMm(problem, number, geo) {
  return measureHeightMm(problemMeasureHtml(problem, number, geo), geo.colWPx, geo);
}

/**
 * 문항 높이를 한 번에 잰다(문항 순서 = 반환 배열 순서).
 * 측정은 (문항, 번호, geo)에 대해 결정적이라 배치 도중 재는 것과 값이 같다 —
 * 사전 계산해 두면 배치를 몇 번 다시 해도 재측정이 없다(면 패턴 조작 즉시 반영).
 */
export function measureProblemHeightsMm(problems, geo) {
  return measureHeightsMm(problems.map((p, i) => [problemMeasureHtml(p, i + 1, geo), geo.colWPx]), geo);
}

function solutionMeasureHtml(sol, geo) {
  // 번호 규격은 렌더(SolutionItemV4 → numberSpecV4)와 단일 소스 — 어긋나면 B4에서 실측이 틀려 단이 넘친다
  const { fontPt, font, weight, text, gapMm } = numberSpecV4(geo, sol.number);
  const html = `
    <div style="display:flex;gap:${gapMm}mm;align-items:flex-start;font-size:${geo.contentFontPt}pt;margin-bottom:${solutionItemGapMm(geo)}mm;padding-bottom:${2 * geo.k}mm;border-bottom:1px solid #eee;">
      <div style="font-size:${fontPt}pt;font-weight:${weight};font-family:${font};line-height:1;min-width:${fontPt}pt;white-space:nowrap;">${text}</div>
      ${sol.content
    ? `<div class="pv4-problem-content" style="flex:1;min-width:0;overflow-wrap:break-word;word-break:break-word;">${sol.content}</div>`
    : `<div style="flex:1;min-width:0;"><div style="font-size:${geo.contentFontPt}pt;line-height:1.5;"><span style="font-size:${geo.contentFontPt}pt;">${solutionPlaceholder(sol)}</span></div></div>`}
    </div>`;
  return html;
}

/**
 * 풀이 높이(mm). 항목 아래 여백은 들어 있지 않다 — 감싸는 .pv4-sheet 높이가 자식의 아래 여백을 포함하지 않기 때문이다.
 * 여백은 distributeSolutionsV4 가 더한다. 한 단에 풀이가 수십 개 들어가므로 정수로 반올림하는 offsetHeight 를 쓰지 않는다
 * (항목마다 최대 0.5px 씩 쌓여 단이 넘친다). 문제 면은 슬롯 배치라 쌓이지 않아 종전대로 둔다.
 */
function measureSolutionHeightsMm(solutions, geo) {
  return measureHeightsMm(
    solutions.map((sol) => [solutionMeasureHtml(sol, geo), geo.colWPx]),
    geo,
    (sheet) => sheet.getBoundingClientRect().height,
  );
}

export function measureSolutionHeightMm(sol, geo) {
  return measureSolutionHeightsMm([sol], geo)[0];
}

/**
 * v2 distributeProblems 이식 — geo 기반. 반환: [{left:[…], right:[…]}]
 *
 * opts(전부 선택):
 *   heights    사전 측정된 문항 높이(mm) 배열. 없으면 여기서 측정한다(기존 호출과 동일).
 *   patternOf  (면 인덱스) → 패턴 키. 주면 면 패턴 배치로 위임한다.
 * 둘 다 없으면 아래 그리디가 그대로 돌아 기존 동작과 완전히 같다 —
 * 측정 호출이 사전 계산된 heights 참조로 바뀐 것 외에 배치 로직은 손대지 않았다.
 */
export function distributeProblemsV4(problems, geo, opts) {
  const heights = (opts && opts.heights) || measureProblemHeightsMm(problems, geo);
  if (opts && opts.patternOf) return distributeByPatterns(problems, geo, heights, opts.patternOf);

  const pages = [];
  let currentPage = { left: [], right: [] };
  let currentPageIndex = 0;
  let problemCounter = 0;

  const slotOf = (pageIndex) => (pageIndex === 0 ? geo.slotFirstH : geo.slotRestH);

  const processPage = (problem, index) => {
    const slotH = slotOf(currentPageIndex);
    const problemHeight = heights[index];

    const isPageFull =
      (currentPage.left.length === 2 && currentPage.right.length === 2) ||
      (currentPage.left.length === 1 && currentPage.left[0]?.useFullColumn && currentPage.right.length === 2) ||
      (currentPage.right.length === 1 && currentPage.right[0]?.useFullColumn);

    if (isPageFull) {
      pages.push({ ...currentPage });
      currentPage = { left: [], right: [] };
      currentPageIndex += 1;
      processPage(problem, index);
      return;
    }

    if (problemHeight > slotH) {
      const longProblem = {
        ...problem,
        startHeight: 0,
        height: problemHeight,
        useFullColumn: true,
        problemNumber: (problemCounter += 1),
      };
      if (currentPage.left.length === 0 && currentPage.right.length === 0) {
        currentPage.left = [longProblem];
      } else if (currentPage.left.length >= 1 && currentPage.right.length === 0) {
        currentPage.right = [longProblem];
      } else {
        pages.push({ ...currentPage });
        currentPage = { left: [longProblem], right: [] };
        currentPageIndex += 1;
      }
      return;
    }

    const addTo = (column) => {
      const isTop = column === 'left' ? currentPage.left.length === 0 : currentPage.right.length === 0;
      const normal = {
        ...problem,
        startHeight: isTop ? 0 : slotH,
        height: problemHeight,
        problemNumber: (problemCounter += 1),
      };
      currentPage[column].push(normal);
    };

    if (currentPage.left.length === 1 && currentPage.left[0].useFullColumn) {
      if (currentPage.right.length < 2) addTo('right');
    } else if (currentPage.right.length === 1 && currentPage.right[0].useFullColumn) {
      pages.push({ ...currentPage });
      currentPage = { left: [], right: [] };
      currentPageIndex += 1;
      processPage(problem, index);
    } else if (currentPage.left.length < 2) {
      addTo('left');
    } else if (currentPage.right.length < 2) {
      addTo('right');
    }
  };

  problems.forEach((p, i) => processPage(p, i));
  if (currentPage.left.length > 0 || currentPage.right.length > 0) pages.push(currentPage);
  return pages;
}

/**
 * 풀이 분배: 항목 단위 그리디 2단 채움(페이지당 좌→우) + **단을 넘는 항목만 슬라이스**.
 *
 * 단에 들어가는 풀이는 통째로 놓는다 — 항목 사이에서만 단이 바뀌므로 경계가 깨끗하다.
 * 단 높이(colH)보다 긴 풀이는 통째로 둘 곳이 없어, v2(printPreview의 "1열 렌더 후
 * 슬라이스")처럼 **단 높이 창으로 쪼개 다음 단·다음 장으로 잇는다**. 쪼개진 항목은
 * `slice: {offset, index, count}` 를 달고 나가고, 렌더(SolutionItemV4)가 같은 본문을
 * offset 만큼 올려 그 창에 해당하는 세로 구간만 보여 준다 — 재측정이 없다.
 * 창 경계가 글줄 중간에 올 수 있는 것은 v2 도 감수하던 트레이드오프다.
 * 긴 항목은 항상 **빈 단에서 시작**한다: 남은 자투리에서 시작하면 첫 창 높이가 매번 달라
 * 경계 위치를 예측할 수 없다(창 높이가 colH 로 일정해야 어긋났을 때 눈에 띈다).
 *
 * 반환: [{left:[sol…], right:[sol…]}]
 */
export function distributeSolutionsV4(solutions, geo) {
  const colH = geo.areaRestH;
  const gap = solutionItemGapMm(geo);
  const pages = [];
  let cols = [[], []];
  let colIdx = 0;
  let used = 0;

  const advanceCol = () => {
    colIdx += 1;
    used = 0;
    if (colIdx > 1) {
      pages.push({ left: cols[0], right: cols[1] });
      cols = [[], []];
      colIdx = 0;
    }
  };

  // 풀이 높이는 루프 전에 한 번에 잰다 — 루프는 순서대로 하나씩 읽기만 한다(배치 규칙은 그대로)
  const solHeights = measureSolutionHeightsMm(solutions, geo);
  solutions.forEach((sol, solIdx) => {
    const h = solHeights[solIdx];

    if (h > colH) {
      if (cols[colIdx].length > 0) advanceCol();
      const count = Math.ceil(h / colH);
      for (let s = 0; s < count; s += 1) {
        const sliceH = Math.min(colH, h - s * colH);
        cols[colIdx].push({ ...sol, height: sliceH, slice: { offset: s * colH, index: s, count } });
        used += sliceH;
        if (s < count - 1) advanceCol();
      }
      used += gap; // 마지막 창 아래 여백 — 렌더(SolutionItemV4)가 마지막 창에만 준다
      return; // 마지막 창 뒤 자투리(used)는 다음 항목이 이어 쓴다
    }

    // 본문이 단 안에 들어오면 놓는다. 맨 아래 항목의 여백은 단 밖으로 나가도 잘릴 것이 없다
    if (used + h > colH && cols[colIdx].length > 0) advanceCol();
    cols[colIdx].push({ ...sol, height: h });
    used += h + gap; // 여백을 빼면 다음 항목이 그만큼 아래로 밀려 단 끝이 잘린다
  });
  if (cols[0].length > 0 || cols[1].length > 0) {
    pages.push({ left: cols[0], right: cols[1] });
  }
  return pages;
}
